import storage from 'local-storage-fallback';
import { isEmpty } from 'lodash-es';
import { parseCookies } from 'nookies';
import { useEffect, useMemo } from 'react';

import type { Video } from 'api';
import { useDidMount } from 'lib/hooks/useDidMount';
import usePersonalizedContent from 'lib/hooks/useSailthru/usePersonalizedContent';
import { SailthruFormattedItem } from 'lib/sailthru';
import { useFallbackData } from 'lib/sailthru/fallback';
import { SailthruSections } from 'lib/sailthru/types';

interface UseContentDataOptions {
  sailthruSectionId?: string;
}

export function useContentData({ sailthruSectionId }: UseContentDataOptions) {
  const hasMounted = useDidMount();
  const allCookies = parseCookies();
  const sailthruCookieValue = storage.getItem('sailthru_hid');
  const hasSailthruCookie = !!(
    sailthruCookieValue || allCookies.sailthru_visitor
  );

  const {
    data: personalizedData,
    refetch: fetchPersonalizedData,
    isFetching: isFetchingPersonalizedData
  } = usePersonalizedContent(sailthruSectionId as string);

  // Fallback data hook - use RecommendationsNewest as default fallback with 8 talks
  const {
    data: fallbackData,
    loading: fallbackLoading,
    fetchFallbackContent
  } = useFallbackData(SailthruSections.RecommendationsNewest, { first: 8 });

  useEffect(() => {
    if (hasSailthruCookie && hasMounted && sailthruSectionId) {
      fetchPersonalizedData();
    }
  }, [fetchPersonalizedData, hasSailthruCookie, hasMounted, sailthruSectionId]);

  // Try fallback when Sailthru data is not available
  useEffect(() => {
    if (
      !isFetchingPersonalizedData &&
      isEmpty(personalizedData) &&
      !fallbackLoading &&
      isEmpty(fallbackData)
    ) {
      fetchFallbackContent?.();
    }
  }, [
    isFetchingPersonalizedData,
    personalizedData,
    fallbackLoading,
    fallbackData,
    fetchFallbackContent
  ]);

  const contentData: SailthruFormattedItem[] = useMemo(() => {
    // Use Sailthru data if available
    if (!isEmpty(personalizedData)) {
      return personalizedData.slice(0, 8); // Get 8 talks (1 hero + up to 7 for right column)
    }

    // Fallback to GraphQL data if Sailthru is unavailable
    if (!isEmpty(fallbackData)) {
      // Format GraphQL data to match SailthruFormattedItem interface
      return fallbackData.slice(0, 8).map((talk: Video) => ({
        ...talk
      }));
    }

    return [];
  }, [
    // Use JSON.stringify to create stable dependencies based on actual content
    JSON.stringify(personalizedData),
    JSON.stringify(fallbackData)
  ]);

  // Memoize content IDs to prevent infinite re-renders
  const contentIds = useMemo(() => {
    return contentData.map(item => String(item?.id)).filter(Boolean);
  }, [JSON.stringify(contentData)]);

  // Show loading state while fetching either Sailthru or fallback data
  const isLoading = isFetchingPersonalizedData || fallbackLoading;

  const heroTalk = contentData[0];
  const ribbonTalks = contentData.slice(1, 8); // Up to 7 talks for right column

  return {
    contentData,
    contentIds,
    isLoading,
    heroTalk,
    ribbonTalks
  };
}
